import { TIME_RANGES } from 'core/components/widgets/TimePicklist'
import TrendGraph from 'core/components/widgets/TrendGraph'
import useTrendData from 'core/components/widgets/useTrendData'
import { formatMetricValue } from 'core/utils/unitUtils'
import React, { useEffect, useMemo, useState } from 'react'

interface Props {
  title: string
  units: string
  metricKey?: string
  resourceId?: string
  stats?: { current: number; max: number; percent: number }
  precision?: number
  resources?: any[]
  tooltipMsg?: string | (() => React.ReactNode)
}

export default function TrendGraphWidget({
  title,
  units,
  metricKey,
  resourceId,
  stats,
  precision = 1,
  resources = [],
  tooltipMsg = '',
}: Props) {
  const [currentTimeRange, setCurrentTimeRange] = useState(TIME_RANGES.last_1_hour)
  const { current, max } = stats || {}

  const showFooterStats = current !== undefined && max !== undefined
  const available = showFooterStats ? max - current : undefined

  const formattedCurrent = current !== undefined ? formatMetricValue(current, units) : undefined
  const formattedMax = max !== undefined ? formatMetricValue(max, units) : undefined
  const formattedAvailable =
    available !== undefined ? formatMetricValue(available, units) : undefined

  const curStr = formattedCurrent?.formattedValue
  const maxStr = formattedMax?.formattedValue
  const availableStr = formattedAvailable?.formattedValue
  const displayUnit = formattedCurrent?.displayUnit || formattedMax?.displayUnit

  const { values, average, reload } = useTrendData({
    metricKey,
    currentTimeRange,
    params: {
      id: resourceId,
    },
    resources,
  })

  const formattedAverage = average !== undefined ? formatMetricValue(average, units) : undefined

  const scaledGraphValues =
    values && values.length > 0
      ? values.map((point: any) => {
          if (point?.value === undefined) return point
          const formatted = formatMetricValue(point.value, units)
          return {
            ...point,
            value: parseFloat(formatted.formattedValue),
          }
        })
      : []

  const finalDisplayUnit =
    displayUnit ||
    formattedAverage?.displayUnit ||
    (scaledGraphValues?.[0] && values?.[0]?.value !== undefined
      ? formatMetricValue(values[0].value, units).displayUnit
      : units)

  // Add event listener for refreshGraph event
  useEffect(() => {
    document.addEventListener('refreshGraph', reload)

    return () => {
      document.removeEventListener('refreshGraph', reload)
    }
  }, [reload])

  // useMemo to prevent re-rendering of graph when resources change
  const graphData = useMemo(() => {
    return {
      title,
      averageValue: formattedAverage?.formattedValue,
      ...(showFooterStats && {
        footerValues: [
          { key: 'PROVISIONED', value: curStr },
          { key: 'AVAILABLE', value: availableStr },
          { key: 'CAPACITY', value: maxStr },
        ],
      }),
      unit: finalDisplayUnit,
      graph: {
        values: scaledGraphValues,
        keys: [
          {
            name: 'value',
            color: '#0089C7',
            legendName: 'Usage',
            offsetFirst: '0%',
            offsetSecond: '113.39%',
            stopOpacityFirst: 0.3,
            stopOpacitySecond: 0.1,
          },
        ],
      },
    }
  }, [
    scaledGraphValues,
    formattedAverage?.formattedValue,
    showFooterStats,
    curStr,
    availableStr,
    maxStr,
    title,
    finalDisplayUnit,
  ])

  return (
    <TrendGraph
      data={graphData}
      onTimeRangeChange={setCurrentTimeRange}
      currentTimeRange={currentTimeRange}
      tooltipMsg={tooltipMsg}
    />
  )
}
